import React from 'react';
import { Avatar, Box, Button, ButtonBase, Typography } from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { motion } from 'framer-motion';
import type { ProfileUser } from '../../services/mediaContracts';
import { renderRichText, type MentionClickHandler } from './richText';
import AccountNavButtons from './AccountNavButtons';

interface ProfileHeaderProps {
  profile: ProfileUser;
  onExtractAll?: () => void;
  extractDisabled?: boolean;
  onOpenFollowers?: () => void;
  onOpenFollowing?: () => void;
  canAccountBack?: boolean;
  canAccountForward?: boolean;
  onAccountBack?: () => void;
  onAccountForward?: () => void;
  onMentionClick?: MentionClickHandler;
}

const formatCount = (n?: number): string => {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  profile,
  onExtractAll,
  extractDisabled,
  onOpenFollowers,
  onOpenFollowing,
  canAccountBack,
  canAccountForward,
  onAccountBack,
  onAccountForward,
  onMentionClick,
}) => {
  const showAccountNav = Boolean(onAccountBack || onAccountForward);

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      sx={{
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid #2F3336',
        backgroundColor: '#16181C',
        mb: 3,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          height: { xs: 96, sm: 128 },
          backgroundColor: '#0F1419',
          backgroundImage: profile.banner_url
            ? undefined
            : 'linear-gradient(135deg, #16181C 0%, #0F1419 50%, #1A2332 100%)',
          overflow: 'hidden',
        }}
      >
        {profile.banner_url && (
          <Box
            component="img"
            src={profile.banner_url}
            alt=""
            referrerPolicy="no-referrer"
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          rowGap: 1.5,
          px: 2,
          pb: 2,
          pt: 0,
          alignItems: 'flex-start',
          mt: { xs: -4, sm: -5 },
        }}
      >
        <Avatar
          src={profile.avatar_url}
          alt={profile.name}
          imgProps={{ referrerPolicy: 'no-referrer' }}
          sx={{
            width: { xs: 64, sm: 80 },
            height: { xs: 64, sm: 80 },
            border: '3px solid #000',
            backgroundColor: '#2F3336',
          }}
        />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            // Clears the row's negative mt (avatar/banner overlap) plus 16px of breathing room
            pt: { xs: 6, sm: 7 },
            pb: 0.5,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: '#E7E9EA',
              lineHeight: 1.2,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
            }}
            noWrap
          >
            {profile.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            @{profile.screen_name}
          </Typography>
        </Box>
        {onExtractAll && (
          <Button
            variant="contained"
            startIcon={<CloudDownloadIcon />}
            onClick={onExtractAll}
            disabled={extractDisabled}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              flexShrink: 0,
              mb: 0.5,
              alignSelf: 'flex-end',
              minHeight: 44,
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Extract all
          </Button>
        )}
      </Box>
      {profile.description && (
        <Typography
          variant="body2"
          component="div"
          sx={{ px: 2, pb: 2, color: '#E7E9EA', lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          {renderRichText(profile.description, { onMentionClick })}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          pb: 2,
          pt: 1,
          borderTop: '1px solid #2F3336',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: { xs: 2, sm: 3 },
            flexWrap: 'wrap',
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <Box component="span" sx={{ color: '#E7E9EA', fontWeight: 600 }}>
              {formatCount(profile.media_count)}
            </Box>{' '}
            Media
          </Typography>
          <ButtonBase
            onClick={onOpenFollowers}
            disabled={!onOpenFollowers}
            sx={{
              borderRadius: 1,
              px: 0.5,
              py: 0.25,
              color: 'inherit',
              fontFamily: 'inherit',
              fontSize: '0.875rem',
              lineHeight: 1.43,
              '&:hover': onOpenFollowers
                ? { textDecoration: 'underline', textDecorationColor: '#71767B' }
                : undefined,
              '&.Mui-disabled': { opacity: 1 },
            }}
          >
            <Box component="span" sx={{ color: '#E7E9EA', fontWeight: 600 }}>
              {formatCount(profile.followers)}
            </Box>
            <Box component="span" sx={{ color: '#71767B', ml: 0.5 }}>
              Followers
            </Box>
          </ButtonBase>
          <ButtonBase
            onClick={onOpenFollowing}
            disabled={!onOpenFollowing}
            sx={{
              borderRadius: 1,
              px: 0.5,
              py: 0.25,
              color: 'inherit',
              fontFamily: 'inherit',
              fontSize: '0.875rem',
              lineHeight: 1.43,
              '&:hover': onOpenFollowing
                ? { textDecoration: 'underline', textDecorationColor: '#71767B' }
                : undefined,
              '&.Mui-disabled': { opacity: 1 },
            }}
          >
            <Box component="span" sx={{ color: '#E7E9EA', fontWeight: 600 }}>
              {formatCount(profile.following)}
            </Box>
            <Box component="span" sx={{ color: '#71767B', ml: 0.5 }}>
              Following
            </Box>
          </ButtonBase>
        </Box>

        {showAccountNav && (
          <Box sx={{ ml: 'auto', flexShrink: 0 }}>
            <AccountNavButtons
              canBack={canAccountBack}
              canForward={canAccountForward}
              onBack={onAccountBack}
              onForward={onAccountForward}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ProfileHeader;
